import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Text, Button, SelectInput, TextInput, Icon, Switch, Status } from '@/ui-kits';
import { NA, statusOptions } from '@/constants';
import { cn, formatDate, formatName } from '@/utils/common_functions';
import { AddAdminRequestPayload, Admin, DataTableColumn, GetAdminsRequestPayload, SelectInputitem, UpdateAdminRequestPayload } from '@/interface';
import { DataTable, AddAdminModal } from '@/components';
import {
  adminDataSelector,
  adminTotalPagesSelector,
  adminTotalResultSelector,
  adminCurrentPageSelector,
  userSuccessSelector,
  userFailureSelector,
  userStatus,
  appAdminRoles,
} from '@/services/redux/selectors';
import { addAdminRequest, deleteAdminRequest, getAdminsRequest, resetUserMessage, updateAdminRequest } from '@/services/redux/slices';
import { ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes } from '@/utils/getMessages';
import { useToast, useConfimationModal } from '@/hooks';

export const AdminScreen: React.FC = () => {
  // ==============================
  // hooks
  // ==============================
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { showModal } = useConfimationModal();
  //==============================
  // selectors
  //==============================
  const adminData = useSelector(adminDataSelector);
  const adminTotalPages = useSelector(adminTotalPagesSelector);
  const adminTotalResult = useSelector(adminTotalResultSelector);
  const adminCurrentPage = useSelector(adminCurrentPageSelector);
  const adminSuccess = useSelector(userSuccessSelector) as SuccessCodes;
  const adminFailure = useSelector(userFailureSelector) as ErrorCodes;
  const isLoading = useSelector(userStatus);
  const adminRoles = useSelector(appAdminRoles);

  // ==============================
  // states
  // ==============================
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<SelectInputitem | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<SelectInputitem | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [page, setPage] = useState(1);
  const [editingRow, setEditingRow] = useState<UpdateAdminRequestPayload | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);

  // Track previous filter values to detect changes
  const prevSearchRef = useRef(search);
  const prevSelectedRoleRef = useRef(selectedRole?.id);
  const prevSelectedStatusRef = useRef(selectedStatus?.id);

  // ==============================
  // functions
  // ==============================
  function resetFilter() {
    setSearch('');
    setSelectedRole(null);
    setSelectedStatus(null);
    setIsFilterActive(false);
    setPage(1);
  }

  const fetchAdmins = useCallback(() => {
    let isFilterApplied = false;
    setErrorMessage('');
    const payload: GetAdminsRequestPayload = {
      page,
    };
    if (search) {
      payload.search_text = search;
      isFilterApplied = true;
    }
    if (selectedRole?.id) {
      payload.role = selectedRole.id;
      isFilterApplied = true;
    }
    if (selectedStatus?.id) {
      payload.status = selectedStatus.id === 1;
      isFilterApplied = true;
    }

    if (isFilterApplied) {
      setIsFilterActive(true);
    } else {
      setIsFilterActive(false);
    }
    dispatch(getAdminsRequest(payload));
  }, [search, selectedRole, selectedStatus, page]);

  const handleSaveNewAdmin = (formData: AddAdminRequestPayload) => {
    dispatch(addAdminRequest(formData));
    setIsAddAdminModalOpen(false);
  };

  const handleEditRow = (row: Admin) => {
    setEditingRow({ id: row.id, name: row.name, email: row.email, phone: row.phone || '', role: row.role.id, status: row.status });
  };

  const handleSaveRow = (row: UpdateAdminRequestPayload) => {
    const payload: UpdateAdminRequestPayload = {
      id: row.id,
      name: row.name,
      email: row.email,
      phone: row.phone,
      role: row.role,
      status: row.status,
    };
    dispatch(updateAdminRequest(payload));
  };
  const handleCancelRow = (row: Admin) => {
    setEditingRow(null);
  };

  const handleEditRowFields = (row: number, field: string, value: any) => {
    setEditingRow(p => {
      if (!p) return null;
      if (row !== p.id) return null;
      return {
        ...p,
        [field]: value,
      };
    });
  };

  const hadleDeleteRow = (row: Admin) => {
    showModal({
      header: `Are you sure you want to\ndelete this user?`,
      onConfirmHandler: () => dispatch(deleteAdminRequest({ id: row.id })),
      // cancelButtonClassName: 'max-w-[100px]',
      // confirmButtonClassName: 'max-w-[100px]',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
    });
  };

  const handleStatusChange = (row: Admin, value: boolean) => {
    showModal({
      onConfirmHandler: () => handleEditRowFields(row.id, 'status', value),
      header: `Are you sure you want to\n${value ? 'activate' : 'block'} this user?`,
      // cancelButtonClassName: 'max-w-[100px]',
      // confirmButtonClassName: 'max-w-[100px]',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
    });
  };

  // ==============================
  // data
  // ==============================
  const columns: DataTableColumn<Admin>[] = useMemo((): DataTableColumn<Admin>[] => {
    return [
      {
        name: 'id',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Admin ID
          </Text>
        ),
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark">
            {row.id}
          </Text>
        ),
        align: 'left',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '70px', maxWidth: '120px' },
      },
      {
        name: 'name',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Name
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <TextInput
              value={editingRow.name}
              onChangeText={value => handleEditRowFields(row.id, 'name', value)}
              wrapperClassName="border-[1px] w-full max-w-[270px] py-0 rounded"
              className={cn('text-sm')}
              inputClassName="py-1 !font-FigtreeRegular text-sm"
              name="name"
            />
          ) : (
            <Text className="font-FigtreeSemiBold text-wrap text-ui_input_border_dark">
              {formatName(row.name) ?? NA}
            </Text>
          ),
        align: 'left',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '200px', maxWidth: '250px' },
      },
      {
        name: 'email',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Email
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <TextInput
              value={editingRow.email}
              onChangeText={value => handleEditRowFields(row.id, 'email', value)}
              wrapperClassName="border-[1px] w-full py-0 rounded"
              className={cn('text-sm')}
              inputClassName="py-1 !font-FigtreeRegular text-sm w-full"
              name="email"
            />
          ) : (
            <Text className="font-FigtreeSemiBold break-words text-wrap text-ui_input_border_dark">
              {row.email ?? NA}
            </Text>
          ),
        align: 'left',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '260px', maxWidth: '300px', width: 'full' },
      },
      {
        name: 'phone',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Phone Number
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <TextInput
              value={editingRow.phone || ''}
              onChangeText={value => handleEditRowFields(row.id, 'phone', value)}
              wrapperClassName="border-[1px] w-full py-0 rounded"
              className={cn('text-sm')}
              inputClassName="py-1 !font-FigtreeRegular text-sm w-full"
              name="phone"
            />
          ) : (
            <Text className="font-FigtreeSemiBold break-words text-wrap text-ui_input_border_dark">
              {row.phone ?? NA}
            </Text>
          ),
        align: 'left',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '200px', maxWidth: '250px' },
      },
      {
        name: 'role',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Role Name
          </Text>
        ),
        render: row => {
          const rowIndex = adminData.findIndex(r => r.id === row.id);
          const isLastFewRows = rowIndex >= 0 && rowIndex >= adminData.length - 3;
          return editingRow?.id === row.id ? (
            <SelectInput
              value={editingRow.role}
              onChange={value => handleEditRowFields(row.id, 'role', value.id)}
              options={adminRoles}
              className="max-w-[200px] !font-FigtreeRegular"
              itemTextClassName="!font-FigtreeRegular"
              wrapperClassName="border py-1 rounded"
              textClassName="!text-sm"
              dropdownPosition={isLastFewRows ? 'above' : 'below'}
              usePortal
            />
          ) : (
            <Text className="font-FigtreeSemiBold text-ui_input_border_dark">
              {row.role.label ?? NA}
            </Text>
          );
        },
        align: 'left',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '200px', maxWidth: '200px' },
      },
      {
        name: 'status',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Status
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <div className="flex items-center justify-center gap-2">
              <Switch value={editingRow.status} onChange={value => handleStatusChange(row, value)} />
            </div>
          ) : (
            <Status status={row.status} />
          ),
        align: 'center',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '100px', maxWidth: '150px' },
      },
      {
        name: 'last_activity',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Last Login
          </Text>
        ),
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark">
            {row.last_activity ? formatDate(row.last_activity) : NA}
          </Text>
        ),
        align: 'center',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '120px', maxWidth: '200px' },
      },
      {
        name: 'action',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">
            Actions
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <div className="flex justify-center w-full gap-4">
              <button className="flex justify-center" onClick={() => handleSaveRow(editingRow)}>
                <Icon type="green_tick_btn" className="size-4" />
              </button>
              <button className="flex justify-center" onClick={() => handleCancelRow(row)}>
                <Icon type="cross_btn_red" className="size-4" />
              </button>
            </div>
          ) : (
            <div className="flex justify-center w-full gap-4">
              <button className="flex justify-center" onClick={() => handleEditRow(row)}>
                <Icon type="edit_input" className="size-4 self-center" />
              </button>
              <button className="flex justify-center" onClick={() => hadleDeleteRow(row)}>
                <Icon type="trash_red" className="size-4 self-center" />
              </button>
            </div>
          ),
        align: 'center',
        width: { minWidth: adminData.length === 0 ? 'fit-content' : '80px', maxWidth: '110px' },
      },
    ];
  }, [editingRow, adminData]);

  // ==============================
  // side effects
  // ==============================
  useEffect(() => {
    const timeout = setTimeout(fetchAdmins, 500);
    return () => clearTimeout(timeout);
  }, [fetchAdmins]);

  // Exit edit mode when filters are changed (not just when filters exist)
  useEffect(() => {
    // Only exit edit mode if filters actually changed while in edit mode
    const filtersChanged =
      prevSearchRef.current !== search ||
      prevSelectedRoleRef.current !== selectedRole?.id ||
      prevSelectedStatusRef.current !== selectedStatus?.id;

    if (filtersChanged && editingRow) {
      setEditingRow(null);
    }

    // Update refs with current values
    prevSearchRef.current = search;
    prevSelectedRoleRef.current = selectedRole?.id;
    prevSelectedStatusRef.current = selectedStatus?.id;
  }, [search, selectedRole, selectedStatus, editingRow]);

  useEffect(() => {
    // if admin success, fetch admins
    if (adminSuccess) {
      if (['S-10008', 'S-10019', 'S-10011'].includes(adminSuccess)) {
        showToast(getSuccessMessage(adminSuccess), 'success');
        fetchAdmins();
      }
      if (adminSuccess === 'S-10008') {
        setIsAddAdminModalOpen(false);
      }
      if (adminSuccess === 'S-10019') {
        setEditingRow(null);
      }
    }
    if (adminFailure) {
      if (['E-10044', 'E-10054'].includes(adminFailure)) {
        setErrorMessage(getErrorMessage(adminFailure));
      } else {
        showToast(getErrorMessage(adminFailure), 'failure');
      }
    }
    return () => {
      dispatch(resetUserMessage());
    };
  }, [adminSuccess, adminFailure]);

  return (
    <div className="flex flex-col gap-4 p-2 sm:p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex flex-col">
          <Text variant="heading" className="mb-1 text-ui_blue">
            Admin Management
          </Text>
          <Text variant="title" className="text-ui_input_border_dark !font-FigtreeRegular whitespace-nowrap">
            Manage All Admins & Maintain Control
          </Text>
        </div>
        <Button title="+ Add Admin" variant="primary" onClick={() => setIsAddAdminModalOpen(true)} className="self-start sm:self-auto" />
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search by name, email or ID"
          className="w-full sm:min-w-[280px] sm:flex-1 sm:flex-shrink-0 lg:max-w-[400px]"
          wrapperClassName="px-2 border"
        />
        <div className="grid grid-cols-2 sm:contents gap-4">
          <SelectInput
            value={selectedRole?.id || ''}
            onChange={setSelectedRole}
            placeholder="All Roles"
            options={adminRoles}
            className="w-full sm:w-auto sm:min-w-[155px] sm:flex-shrink-0"
            wrapperClassName="border"
          />
          <SelectInput
            value={selectedStatus?.id || ''}
            onChange={setSelectedStatus}
            placeholder="All Status"
            options={statusOptions}
            className="w-full sm:w-auto sm:min-w-[140px] sm:flex-shrink-0"
            wrapperClassName="border"
          />
        </div>

        {isFilterActive && (
          <Button
            title="Reset Filters"
            variant="secondary"
            onClick={resetFilter}
            className="w-fit sm:w-auto sm:flex-shrink-0"
          />
        )}
      </div>

      <DataTable
        columns={columns}
        data={adminData}
        totalPages={adminTotalPages}
        currentPage={adminCurrentPage}
        totalResult={adminTotalResult}
        onPageChange={setPage}
        errorMessage={errorMessage}
        loading={isLoading}
      />

      <AddAdminModal
        open={isAddAdminModalOpen}
        onClose={() => setIsAddAdminModalOpen(false)}
        onSave={handleSaveNewAdmin}
        roleOptions={adminRoles}
        statusOptions={statusOptions}
      />
    </div>
  );
};
